import React, { useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  FONT_SIZE, FONT_WEIGHT, SPACING, RADIUS, LINE_HEIGHT, isDrop, textOnFill,
} from '../../constants/constants';
import { Card, DROP_SEVERITY_LABEL } from '../../types/card';
import { useIsPhone } from '../../hooks/UseIsCompact';
import { groupDropCards, type DropGroups } from './dropGroups';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useStorePriceName } from '../../hooks/UseStorePriceName';
import { useCardPage } from '../../contexts/CardPageContext';
import { useCardEdit } from '../../contexts/CardEditContext';
import { formatPrice } from '../../utils/format';
import { NumberCell, NUMBER_CELL_STYLE, formatNumber, numberHeader } from '../common/NumberCell';
import { StatusBadge, type WorkStatus } from '../common/StatusBadge';
import { daysSince, isReviewGateSkip } from '../../utils/daily';
import { AppButton } from '../common/AppButton';
import { READ_ONLY_REASON } from '../../constants/readOnly';
import { thumbnailColumn, TableEmptyState, TableSkeleton } from '../common/TableHelpers';
import { TablePager, VIRTUAL_FROM_PAGE_SIZE, columnsScrollX, useTablePaging, useVirtualScrollY } from '../common/TablePager';
import { MarketRangeBar } from './MarketRangeBar';
import { HubRequestButton, PriceHeldTag, needsHubRequest } from '../common/PriceHeld';

// 다섯째 탭 "PriceHub 변경" 은 "매장가 요청" 메뉴로 옮겨 갔다(감사 #117) — 남아 있던 슬롯 경로를 지웠다(감사 #122).
export type DropTableTab = 'suspect' | 'in_range' | 'rise' | 'no_ref';
type CardTableTab = DropTableTab;

// ─── Badge ────────────────────────────────────────────────────────────────────

const Badge: React.FC<{ text: string; color: string; border: string }> = ({ text, color, border }) => (
  <span style={{
    fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color,
    border: `1px solid ${border}`, borderRadius: RADIUS.sm, padding: '1px 7px', whiteSpace: 'nowrap',
  }}>
    {text}
  </span>
);

// ─── DropReviewTable ──────────────────────────────────────────────────────────

/**
 * "그룹별 목록 표" — DailyWorkflowPage(가격 조회) 전용. 하락 카드 3버킷(확인 필요 /
 * 시세 범위 내 / 참조 없음)과 가격 상승을 하나의 탭 전환 표로 보여준다("하락 검토
 * 개선안" 업데이트, 2026-08-31). 분류만 보여주고 목록을 감추지 않는다 — 일괄 적용
 * 대상도, 확인 불필요한 상승 건도 반영 전에 눈으로 확인할 수 있어야 한다.
 *
 * 확인 필요 탭만 행별 "확인" 버튼(재설계된 모달과 상태 공유), 나머지 3개 탭은 읽기 전용.
 * 탭 상태는 부모(DailyWorkflowPage)가 들고 있어 분류 패널의 "목록 보기"와 연동된다.
 */
const PANEL_ID = 'drop-review-panel';
const tabId = (k: DropTableTab) => `drop-review-tab-${k}`;

export const DropReviewTable: React.FC<{
  tab: DropTableTab;
  onTabChange: (t: DropTableTab) => void;
  scrollY?: string | number;
  /**
   * 카드 목록 첫 조회가 실패했을 때(보여 줄 카드가 없음) 표 자리에 대신 그릴 오류 화면(감사 card-controltower#78).
   * 이때 탭 건수는 "—".
   */
  cardTabsFallback?: React.ReactNode;
  /**
   * 저장 필터 칩의 조건(가격 목록 개편 PR 3, 감사 card-controltower#697) — 네 탭 모두에 함께 건다. 탭 건수는 필터 뒤 수, 칩 건수는
   * 필터 전 전체. 없으면 필터 없음.
   */
  viewFilter?: ((c: Card) => boolean) | null;
  /** 필터 줄에 보일 칩 이름(예: '하락 검토'). */
  viewLabel?: string;
  /** 필터 줄의 '필터 지우기'. */
  onClearView?: () => void;
  /**
   * 매장 가격 요청의 [반영] 에서 온 카드(코덱 리뷰 #869) — 그 카드가 든 탭 · 페이지로 옮겨 줄을 표시하고 화면에 보이게 굴린다.
   * ③ 은 반영 대상 전체를 보내므로 선택을 바꾸지는 않는다. 표에 없으면 안내만.
   */
  focus?: { cardId: number; code: string; name: string } | null;
  /** 안내 줄의 '닫기'. */
  onFocusClose?: () => void;
}> = ({ tab, onTabChange, scrollY, cardTabsFallback, viewFilter, viewLabel, onClearView, focus, onFocusClose }) => {
  const t = useThemeTokens();
  const sp = useStorePriceName();
  // 휴대폰 폭(≤640px)은 표 대신 한 열 카드형(개편 §2-5 · P8) — 상품 · 두 가격 · 상태 · 처리만, 시세 범위 · 사유는 '자세히' 로.
  const phone = useIsPhone();
  const { cards, openReviewFor } = useCardPage();
  const edit = useCardEdit();

  const allGroups = useMemo(() => groupDropCards(cards.cards), [cards.cards]);
  const groups: DropGroups = useMemo(() => {
    if (!viewFilter) return allGroups;
    return {
      suspect: allGroups.suspect.filter(viewFilter),
      inRange: allGroups.inRange.filter(viewFilter),
      rise: allGroups.rise.filter(viewFilter),
      noRef: allGroups.noRef.filter(viewFilter),
    };
  }, [allGroups, viewFilter]);

  const inRangeApplied = groups.inRange.filter((c) => !edit.pendingConfirmIds.has(c.id)).length;
  const suspectDecided = groups.suspect.filter((c) => !edit.pendingConfirmIds.has(c.id)).length;
  // 상승 이상치 — 서버 검토 목록이 있으면 그 판정(gated · 검토 상태), 없으면 카드의 priceReview 만(감사 #566).
  const isAdmin = edit.serverReview?.canReview === true;
  const reviewById = edit.serverReview?.reviewById;
  // 서버 목록에 없는 상승이라도 ③ 이 검토 게이트로 건너뛴 카드(예: 타 샵 최고가 초과 — 백엔드 게이트가 목록 밖에서 막는 것)는
  // '확인 필요' 로 보이고 적용 · 유지를 고를 수 있게 한다(감사 #661, 백엔드 계약은 지후에게 요청). 목록을 아직 못 받았으면
  // 상승률 50% 초과로 대신 판단한다(UseCardData 의 ③ 건너뜀 안내와 맞춘다).
  const riseGated = (c: Card): boolean => {
    const r = reviewById?.get(c.id);
    if (r) return r.gated;
    if (isReviewGateSkip(c.naverError)) return true;
    const sr = edit.serverReview;
    return sr != null && sr.supported !== false && sr.list == null && (c.priceChangeRate ?? 0) > 50;
  };
  // 상승 검토 저장 중인 카드 — '적용' · '유지' 연속 클릭으로 두 번 보내지 않게(감사 #659).
  const [riseBusy, setRiseBusy] = useState<ReadonlySet<number>>(() => new Set());
  const runRise = async (c: Card, d: 'APPLY' | 'KEEP') => {
    if (riseBusy.has(c.id) || !edit.serverReview) return;
    setRiseBusy((prev) => new Set(prev).add(c.id));
    try {
      if (d === 'KEEP') await edit.serverReview.reviewRise(c, d, (saved) => cards.mergeResults([saved]));
      else await edit.serverReview.reviewRise(c, d);
    } finally {
      setRiseBusy((prev) => { const next = new Set(prev); next.delete(c.id); return next; });
    }
  };
  const riseDecision = (c: Card): 'KEEP' | 'APPLY' | null => {
    const r = reviewById?.get(c.id);
    if (r && r.reviewState !== 'PENDING') return r.reviewState;
    return r ? null : (c.priceReview ?? null);
  };
  // 상승이 막힌 까닭 — 서버 검토 목록의 riseReason(백엔드 PR #85, 감사 #708)을 먼저 쓰고, 모르면(옛 서버 · 목록 밖 ③ 건너뜀 · 목록 전)
  // 상승률로 짐작한다. 타 샵 최고가는 OVER_MARKET_MAX 판정에 쓴 값(7일 안)만 — 서버가 null 이면 보이지 않는다.
  const riseMarketMax = (c: Card): { max: number; shops: number | null; collectedAt: string | null } | null => {
    const r = reviewById?.get(c.id);
    return r && r.kind === 'RISE' && r.marketMax != null
      ? { max: r.marketMax, shops: r.marketShopCount, collectedAt: r.marketCollectedAt }
      : null;
  };
  const riseGateNote = (c: Card): string => {
    const reason = reviewById?.get(c.id)?.riseReason ?? null;
    const tail = '③ 이 확인 없이 보내지 않습니다 — 적용 · 유지를 골라 주세요.';
    if (reason === 'RATE_OVER_CAP') return `상승 폭이 커(상한 50% 초과) ${tail}`;
    if (reason === 'OVER_MARKET_MAX') {
      const m = riseMarketMax(c);
      return m
        ? `${sp}이 타 샵 최고가 ${formatPrice(m.max)}보다 높아 ${tail}`
        : `${sp}이 타 샵 최고가보다 높아 ${tail}`;
    }
    if ((c.priceChangeRate ?? 0) > 50) return `상승 폭이 커(상한 50% 초과) ${tail}`;
    return `상승 폭 상한(50%)을 넘었거나 타 샵 최고가보다 높아 ${tail}`;
  };

  const nameColumn: ColumnsType<Card>[number] = {
    title: '상품명', key: 'name', minWidth: 240, ellipsis: true,
    render: (_, c) => (
      <div style={{ minWidth: 0 }}>
        <div style={{
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          fontWeight: FONT_WEIGHT.medium, color: t.textPrimary,
        }}>
          {c.productName}
        </div>
        <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, marginTop: 2 }}>
          {c.sellerProductCode}{c.stockQuantity != null ? ` · 재고 ${c.stockQuantity.toLocaleString('ko-KR')}` : ''}
          {c.priceHeld && <>{' '}<PriceHeldTag t={t} testId={`price-held-${c.id}`} /></>}
        </div>
      </div>
    ),
  };

  // 가격 열은 두 칸으로 나눈다(가격 목록 개편 D2) — 열 머리가 뜻(네이버 가격 · <매장> 가격)과 단위(원)를 말하고, 칸은 숫자만
  // 오른쪽 정렬 · 고정폭(NumberCell). 값이 없으면 '미수집'(0원과 구분, D4).
  const naverPriceColumn: ColumnsType<Card>[number] = {
    title: <span style={{ whiteSpace: 'nowrap' }}>{numberHeader('네이버 가격')}</span>, key: 'naverPrice', width: 136, align: 'right',
    render: (_, c) => <NumberCell value={c.currentPrice} muted testId={`naver-price-${c.id}`} />,
  };

  const storePriceColumn = (rise: boolean, noRef: boolean): ColumnsType<Card>[number] => ({
    title: <span style={{ whiteSpace: 'nowrap' }}>{numberHeader(sp)}</span>,
    key: 'price', width: 136, align: 'right',
    render: (_, c) => {
      const color = rise ? t.accent : noRef ? t.textMuted : t.danger;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <NumberCell value={c.suggestedPrice} bold color={color} testId={`store-price-${c.id}`} />
          {c.priceChangeRate != null && (
            <span style={{ ...NUMBER_CELL_STYLE, fontSize: FONT_SIZE.xs, color }} title="네이버 가격 대비 변동률">
              {rise ? '▲' : '▼'}{formatNumber(Math.abs(c.priceChangeRate), { kind: 'percent' })}
            </span>
          )}
          {/* ③ 이 서버 검토 게이트로 건너뜀(백엔드 PR #79) — 실패가 아니라 검토가 필요한 것(감사 #566). 동기화 상태(점 + 글자). */}
          {isReviewGateSkip(c.naverError) && (
            <StatusBadge axis="sync" status="skipped" label="③ 건너뜀 — 검토 필요"
              testId={`gate-skip-${c.id}`} detail={c.naverError ?? undefined} />
          )}
        </div>
      );
    },
  });

  /** '시세 오래됨(수집 N일 전)' — 7일 넘은 시세는 근거에서 뺐다(백엔드 PR #79 M3, 감사 #566). */
  const staleNote = (c: Card) => {
    if (!c.marketStale) return null;
    const d = daysSince(c.marketCollectedAt);
    return (
      <div data-testid={`market-stale-${c.id}`} style={{ fontSize: FONT_SIZE.xs, color: t.warning, whiteSpace: 'nowrap' }}
        title="PriceHub 의 타 샵 시세 수집이 오래돼(7일 초과) 대조 근거에서 뺐습니다">
        시세 오래됨{d != null ? `(수집 ${d}일 전)` : ''}
      </div>
    );
  };

  const rangeColumn = (hasBar: boolean): ColumnsType<Card>[number] => ({
    title: '타 샵 시세 범위', key: 'range', width: 200,
    render: (_, c) => {
      if (!hasBar) {
        return (
          <div>
            <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>대조할 판매처 없음</span>
            {staleNote(c)}
          </div>
        );
      }
      return (
        <div>
          <MarketRangeBar
            marketMin={c.marketMin} marketMax={c.marketMax}
            currentPrice={c.currentPrice} suggestedPrice={c.suggestedPrice}
            height={18}
          />
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, whiteSpace: 'nowrap' }}>
            {c.marketMin != null && c.marketMax != null
              ? `${formatPrice(c.marketMin)} ~ ${formatPrice(c.marketMax)} · ${c.marketShopCount ?? 0}곳`
              : `참조 ${c.marketShopCount ?? 0}곳`}
          </div>
          {staleNote(c)}
        </div>
      );
    },
  });

  const suspectColumns: ColumnsType<Card> = [
    { ...thumbnailColumn(t), width: 72 },
    nameColumn,
    naverPriceColumn,
    storePriceColumn(false, false),
    rangeColumn(true),
    {
      title: '의심 사유', key: 'reason', minWidth: 160,
      render: (_, c) => {
        // 보호 중 · 하락 아님(#558 M2) — 의심 사유 대신 두 가격을 글로.
        if (!isDrop(c.priceChangeStatus)) {
          return (
            <div data-testid={`held-reason-${c.id}`} style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, lineHeight: LINE_HEIGHT.normal }}>
              네이버 가격 {formatNumber(c.currentPrice, { withUnit: true })}을 지키는 중 — PriceHub 는{' '}
              {formatNumber(c.hubPrice, { withUnit: true })}. 같게 하려면 매장 가격 요청.
            </div>
          );
        }
        const sev = c.dropSeverity;
        const color = sev === 'SEVERE' ? t.danger : sev === 'MODERATE' ? t.warning : t.textSecondary;
        const border = sev === 'SEVERE' ? t.lineDanger : sev === 'MODERATE' ? t.lineWarning : t.border;
        return (
          <div>
            <Badge text={sev ? DROP_SEVERITY_LABEL[sev] : '의심'} color={color} border={border} />
            <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginTop: 4, lineHeight: LINE_HEIGHT.normal }}>
              {c.dropReason ?? '타 샵 시세를 벗어남'}
            </div>
          </div>
        );
      },
    },
    {
      title: '처리', key: 'action', width: 140, align: 'right',
      render: (_, c) => {
        // 보호 중 · 하락 아님(#558 M2) — 다시 검토(대기에 넣기)하면 검토 창 큐에 없는 카드가 ③ 을 막는다. 요청 버튼만.
        if (!isDrop(c.priceChangeStatus)) return <HubRequestButton card={c} t={t} />;
        const decided = !edit.pendingConfirmIds.has(c.id);
        // 누른 줄의 카드로 연다(감사 card-controltower#457 — 카드 id 없이 열어 다른 카드부터 보였다).
        if (!decided) {
          return <AppButton variant="primary" size="sm" onClick={() => openReviewFor(c.id)}>확인</AppButton>;
        }
        const kept = c.suggestedPrice != null && c.suggestedPrice === c.currentPrice;
        // 처리한 카드는 검토 큐에서 빠져 있다 — 다시 확인 대기로 돌려 그 카드로 연다(재검토).
        const again = (
          // 다시 검토는 대기에 넣는 쓰기 — 조회 전용은 잠근다(유저 플로우 제안 7).
          <AppButton
            variant={kept ? 'default' : 'text'} size="sm"
            title={isAdmin ? '다시 검토' : undefined}
            disabled={!isAdmin} disabledReason={isAdmin ? undefined : READ_ONLY_REASON}
            onClick={() => { if (!isAdmin) return; edit.restorePendingMany([c.id]); openReviewFor(c.id); }}
          >
            {kept ? '✗ 유지' : `✓ ${c.suggestedPrice != null ? formatPrice(c.suggestedPrice) : ''}`}
          </AppButton>
        );
        // 유지한 카드가 PriceHub 와 다르면 그 자리에서 요청도(#558 M2).
        if (!needsHubRequest(c)) return again;
        return (
          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: SPACING.xxs }}>
            {again}
            <HubRequestButton card={c} t={t} />
          </div>
        );
      },
    },
  ];

  // 상태 칸은 업무 상태 알약(StatusBadge axis=work) — 색 + 글자, 상태 → 색은 StatusBadge 한 곳(개편 §2-4).
  const statusText = (label: string, status: WorkStatus) => (
    <StatusBadge axis="work" status={status} label={label} style={{ whiteSpace: 'nowrap' }} />
  );

  const inRangeColumns: ColumnsType<Card> = [
    { ...thumbnailColumn(t), width: 72 },
    nameColumn,
    naverPriceColumn,
    storePriceColumn(false, false),
    rangeColumn(true),
    {
      title: '판정', key: 'verdict', minWidth: 200,
      render: (_, c) => (
        <div>
          <Badge text="범위 내" color={t.success} border={t.lineSuccess} />
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginTop: 4, lineHeight: LINE_HEIGHT.normal }}>
            {c.marketMin != null && c.marketMax != null
              ? `${sp}이 ${formatPrice(c.marketMin)}~${formatPrice(c.marketMax)} 사이에 있습니다.`
              : '타 샵 시세 범위 안입니다.'}
          </div>
        </div>
      ),
    },
    {
      title: '상태', key: 'state', width: 104, align: 'right',
      render: (_, c) => edit.pendingConfirmIds.has(c.id)
        ? statusText('적용 대기', 'queued')
        : statusText('✓ 적용됨', 'applied'),
    },
  ];

  // 상승 탭의 시세 칸 — 하락처럼 범위 막대가 아니라 타 샵 최고가 한 값(서버가 OVER_MARKET_MAX 판정에 쓴 것, 감사 #708).
  const riseRangeColumn: ColumnsType<Card>[number] = {
    title: '타 샵 최고가', key: 'range', width: 200,
    render: (_, c) => {
      const m = riseMarketMax(c);
      if (!m) {
        return <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>대조할 판매처 없음</span>;
      }
      const d = daysSince(m.collectedAt);
      return (
        <div data-testid={`rise-market-${c.id}`} style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, whiteSpace: 'nowrap' }}>
          <span style={NUMBER_CELL_STYLE}>{formatPrice(m.max)}</span>
          {m.shops != null ? ` · ${m.shops}곳` : ''}
          {d != null && <div style={{ color: t.textMuted }}>수집 {d === 0 ? '오늘' : `${d}일 전`}</div>}
        </div>
      );
    },
  };

  const riseColumns: ColumnsType<Card> = [
    { ...thumbnailColumn(t), width: 72 },
    nameColumn,
    naverPriceColumn,
    storePriceColumn(true, false),
    riseRangeColumn,
    {
      title: '상승 폭', key: 'delta', minWidth: 200,
      render: (_, c) => (
        <div>
          <Badge
            text={`▲${Math.abs(Math.round(c.priceChangeRate ?? 0))}%`}
            color={t.accent} border={t.lineAccent}
          />
          <div data-testid={`rise-note-${c.id}`} style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginTop: 4, lineHeight: LINE_HEIGHT.normal }}>
            {riseGated(c)
              ? riseGateNote(c)
              : '가격이 올라 손실 위험이 없어 확인 없이 반영됩니다.'}
          </div>
        </div>
      ),
    },
    {
      title: '상태', key: 'state', width: 128, align: 'right',
      render: (_, c) => {
        // 상승 이상치 검토(백엔드 PR #79 Q3 — ③ 은 APPLY 없이는 건너뜀, 감사 #566).
        const decided = riseDecision(c);
        if (decided === 'APPLY') return statusText('✓ 적용 확인', 'applied');
        if (decided === 'KEEP') return statusText('✗ 유지', 'kept');
        if (!riseGated(c)) return statusText('반영 예정', 'scheduled');
        if (!isAdmin) return statusText('확인 필요', 'review');
        return (
          <div data-testid={`rise-review-${c.id}`} style={{ display: 'inline-flex', gap: SPACING.xxs }}>
            <AppButton size="sm" variant="primary" title={`${c.suggestedPrice != null ? formatPrice(c.suggestedPrice) : ''}로 ③ 에 보내기`}
              disabled={riseBusy.has(c.id)} loading={riseBusy.has(c.id)}
              onClick={() => { void runRise(c, 'APPLY'); }}>
              적용
            </AppButton>
            <AppButton size="sm" variant="default" title="네이버 가격 그대로 유지"
              disabled={riseBusy.has(c.id)}
              onClick={() => { void runRise(c, 'KEEP'); }}>
              유지
            </AppButton>
          </div>
        );
      },
    },
  ];

  const noRefColumns: ColumnsType<Card> = [
    { ...thumbnailColumn(t), width: 72 },
    nameColumn,
    naverPriceColumn,
    storePriceColumn(false, true),
    rangeColumn(false),
    {
      title: '사유', key: 'reason', minWidth: 200,
      render: () => (
        <div>
          <Badge text="검증 불가" color={t.textMuted} border={t.border} />
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginTop: 4, lineHeight: LINE_HEIGHT.normal }}>
            PriceHub에 등록된 판매처가 없어 대조할 수 없습니다.
          </div>
        </div>
      ),
    },
    {
      title: '상태', key: 'state', width: 104, align: 'right',
      // ③ 스토어 반영(DailyWorkflowBar)이 참조 없음 카드를 대상에서 뺀다(2026-10-21) — 표기와 동작을 맞췄다.
      render: () => statusText('반영 제외', 'excluded'),
    },
  ];

  // 가격 상승 탭 머리 — 상태 칸과 같은 판정(확인 필요 = 막힌 상승 중 결정 전, 유지 = KEEP, 나머지는 반영 예정 · 적용 확인).
  const riseCounts = groups.rise.reduce((acc, c) => {
    const d = riseDecision(c);
    if (d === 'KEEP') acc.kept += 1;
    else if (d == null && riseGated(c)) acc.review += 1;
    else acc.scheduled += 1;
    return acc;
  }, { scheduled: 0, review: 0, kept: 0 });

  const TABS: Record<CardTableTab, {
    label: string; color: string; count: number; meta: string; note: string;
    rows: Card[]; columns: ColumnsType<Card>; empty: string;
  }> = {
    suspect: {
      label: '확인 필요', color: t.warning, count: groups.suspect.length,
      meta: `${suspectDecided} / ${groups.suspect.length}건 확인 완료`,
      note: `${sp}이 타 샵 시세 범위를 벗어났거나 참고 판매처가 부족한 건입니다. 의심도 높은 순으로 정렬됩니다.`,
      rows: groups.suspect, columns: suspectColumns, empty: '확인이 필요한 하락 카드가 없습니다',
    },
    in_range: {
      label: '시세 범위 내', color: t.success, count: groups.inRange.length,
      meta: inRangeApplied === groups.inRange.length && groups.inRange.length > 0
        ? `${groups.inRange.length.toLocaleString()}건 적용됨`
        : `${groups.inRange.length.toLocaleString()}건 적용 대기`,
      note: `${sp}이 타 샵 판매가 최저~최고 사이에 있는 건입니다. 개별 확인 없이 일괄 적용 대상이지만, 반영 전에 목록으로 확인할 수 있습니다.`,
      rows: groups.inRange, columns: inRangeColumns, empty: '시세 범위 내로 분류된 카드가 없습니다',
    },
    rise: {
      label: '가격 상승', color: t.accent, count: groups.rise.length,
      meta: [
        `${riseCounts.scheduled.toLocaleString()}건 반영 예정`,
        riseCounts.review > 0 ? `${riseCounts.review.toLocaleString()}건 확인 필요` : null,
        riseCounts.kept > 0 ? `${riseCounts.kept.toLocaleString()}건 유지` : null,
      ].filter(Boolean).join(' · '),
      note: `${sp}이 네이버 가격보다 오른 건입니다. 가격이 오르면 손실 위험이 없어 개별 확인 없이 ③ 단계에서 함께 반영됩니다 — 상승 폭이 상한(50%)을 넘거나 타 샵 최고가보다 높은 건만 적용 · 유지를 골라야 보냅니다.`,
      rows: groups.rise, columns: riseColumns, empty: '가격이 오른 카드가 없습니다',
    },
    no_ref: {
      label: '참조 없음', color: t.textMuted, count: groups.noRef.length,
      meta: `${groups.noRef.length.toLocaleString()}건 반영 제외`,
      note: `대조할 판매처 데이터가 없어 ${sp}을 검증할 수 없는 건입니다. ③ 스토어 반영에서 빠지고 네이버 가격이 유지됩니다 — 판매처가 확보되면 다음 조회에서 분류됩니다.`,
      rows: groups.noRef, columns: noRefColumns, empty: '참조 시세 없음으로 분류된 카드가 없습니다',
    },
  };

  const active = TABS[tab];
  // 1280 에서 처리 · 상태 열이 가로 스크롤 뒤로 숨던 것(감사 card-controltower#444) — 'max-content' 면 긴 상품명이 줄지 않고 표가 늘어
  // 말줄임이 먹지 않았다. 열 폭 합을 표 최소 폭으로 두고(이름이 남는 폭을 받으며 말줄임), 마지막(처리 · 상태) 열은 오른쪽에 고정한다.
  const tableColumns = useMemo(() => active.columns.map((c, i) => (
    i === active.columns.length - 1 ? { ...c, fixed: 'right' as const } : c
  )), [active.columns]);
  const tableScrollX = columnsScrollX(active.columns as Array<{ width?: number | string; minWidth?: number }>);

  // 휴대폰 카드형 — 그 탭의 열 render 를 그대로 다시 써서(값 · 버튼이 표와 같게) 한 열에 쌓는다. 앞 둘(이미지 · 상품명)은 머리, 가격 둘은
  // 한 줄, 마지막(상태 · 처리)은 아래, 나머지(시세 범위 · 사유)는 '자세히' 를 펼쳐 본다(개편 §2-5 — Retool 확장 행).
  const phoneColumns: ColumnsType<Card> = useMemo(() => {
    const cols = active.columns as Array<{ key?: React.Key; render?: (v: unknown, c: Card, i: number) => React.ReactNode }>;
    const byKey = (k: string) => cols.find((c) => c.key === k);
    const cell = (k: string, c: Card, i: number) => byKey(k)?.render?.(undefined, c, i) ?? null;
    const last = cols[cols.length - 1];
    const extra = cols.filter((c) => !['thumbnail', 'name', 'naverPrice', 'price'].includes(String(c.key)) && c !== last);
    return [{
      title: '상품 · 가격', key: 'card',
      render: (_: unknown, c: Card, i: number) => (
        // 카드 안 버튼 · 체크박스는 누르는 곳 44px(index.css .drop-card — 시우 검수 2026-11-02 낮음: '매장 가격 요청' 26px · 체크 16px).
        <div data-testid={`drop-card-${c.id}`} className="drop-card" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xxs, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: SPACING.xs, minWidth: 0 }}>
            <div style={{ flex: 'none' }}>{cell('thumbnail', c, i)}</div>
            <div style={{ minWidth: 0, flex: 1 }}>{cell('name', c, i)}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: SPACING.sm }}>
            <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
              네이버 가격
              <div style={{ fontSize: FONT_SIZE.sm }}>{cell('naverPrice', c, i)}</div>
            </div>
            <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, textAlign: 'right' }}>
              {sp}
              <div style={{ fontSize: FONT_SIZE.sm }}>{cell('price', c, i)}</div>
            </div>
          </div>
          {last?.render && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', minHeight: 44, alignItems: 'center' }}>
              {last.render(undefined, c, i)}
            </div>
          )}
          {extra.length > 0 && (
            <details>
              <summary style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, minHeight: 44, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                자세히
              </summary>
              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs }}>
                {extra.map((col) => <div key={String(col.key)}>{col.render?.(undefined, c, i)}</div>)}
              </div>
            </details>
          )}
        </div>
      ),
    }];
  }, [active.columns, sp, t]);

  // 칩 조건으로 지금 탭이 비고 다른 탭에 줄이 있으면 그 탭으로(예: '하락 검토' 칩인데 '가격 상승' 탭을 보고 있던 경우) — 빈 탭을
  // 보고 '없다' 고 오해하지 않게. 칩을 고를 때만(탭을 손으로 고른 뒤에는 그대로 둔다).
  const order: CardTableTab[] = ['suspect', 'in_range', 'rise', 'no_ref'];
  useEffect(() => {
    if (!viewFilter || cardTabsFallback) return;
    if (TABS[tab].rows.length > 0) return;
    const next = order.find((k) => TABS[k].rows.length > 0);
    if (next && next !== tab) onTabChange(next);
    // 칩이 바뀔 때만(이름으로 본다 — 조건 함수는 검토 대기가 바뀔 때도 새로 만들어진다). 줄 수가 바뀔 때마다 옮기면 손으로 고른 탭이 튄다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewLabel, viewFilter == null]);
  const viewTotal = groups.suspect.length + groups.inRange.length + groups.rise.length + groups.noRef.length;
  // 첫 조회 중(성공한 적 없음)은 스켈레톤 줄, 재조회는 기존 줄을 남기고 위에 얇은 진행 줄(개편 §2-6).
  const firstLoad = cards.loading && !cards.hasLoadedOnce;
  const reloading = cards.loading && !firstLoad;
  const tabButtons: { key: DropTableTab; label: string; color: string; count: number }[] =
    order.map((k) => ({ key: k, label: TABS[k].label, color: TABS[k].color, count: TABS[k].count }));
  const activeKey: DropTableTab = tab;
  // 표 위 · 아래 페이지 넘김(감사 card-controltower#115 · #116) — 탭 · 칩을 바꾸면 1페이지, 크기는 남긴다.
  const paging = useTablePaging(active.rows, `${activeKey}|${viewFilter ? viewLabel ?? 'view' : ''}`);
  // 500줄 이상 페이지는 보이는 줄만 그린다(가상 스크롤) — 1,000줄 한 번에 그리기가 약 1초 멈췄다.
  const virtualY = useVirtualScrollY(scrollY);
  const virtual = paging.pageSize >= VIRTUAL_FROM_PAGE_SIZE && paging.pageRows.length > 100;

  // 매장 가격 요청에서 온 카드(코덱 리뷰 #869) — 그 카드가 든 탭으로 → 그 페이지로 → 줄을 화면 가운데로. 한 번만(손으로 탭을 옮긴 뒤엔 그대로).
  const focusTab: DropTableTab | null = focus
    ? order.find((k) => TABS[k].rows.some((c) => c.id === focus.cardId)) ?? null
    : null;
  const [focusDone, setFocusDone] = useState<number | null>(null);
  useEffect(() => {
    if (!focus || firstLoad || cardTabsFallback || focusDone === focus.cardId || !focusTab) return;
    if (focusTab !== tab) { onTabChange(focusTab); return; }
    const idx = active.rows.findIndex((c) => c.id === focus.cardId);
    if (idx < 0) return;
    const pg = Math.floor(idx / paging.pageSize) + 1;
    if (paging.page !== pg) { paging.setPage(pg); return; }
    setFocusDone(focus.cardId);
    const id = focus.cardId;
    requestAnimationFrame(() => {
      const row = document.querySelector(`[data-row-key="${id}"]`) as HTMLElement | null;
      if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    // 탭 · 페이지 · 목록이 바뀔 때 다시 본다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, focusTab, tab, firstLoad, cardTabsFallback, paging.page, paging.pageSize, active.rows, focusDone]);
  const focusRow = (c: Card) => (focus && c.id === focus.cardId
    ? { 'data-focused': 'true', 'aria-current': 'true' as const, style: { outline: `2px solid ${t.accent}`, outlineOffset: -2 } }
    : {});

  // 탭 키보드 이동(WAI-ARIA tabs) — 좌우 화살표로 이웃 탭, Home/End 로 처음 · 끝. 고르면 바로 그 탭을 연다.
  const onTabKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = tabButtons.map((b) => b.key);
    const i = keys.indexOf(activeKey);
    const next = e.key === 'ArrowRight' ? keys[(i + 1) % keys.length]
      : e.key === 'ArrowLeft' ? keys[(i - 1 + keys.length) % keys.length]
        : e.key === 'Home' ? keys[0]
          : e.key === 'End' ? keys[keys.length - 1]
            : null;
    if (!next) return;
    e.preventDefault();
    onTabChange(next);
    document.getElementById(tabId(next))?.focus();
  };

  return (
    <div style={{
      background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, overflow: 'hidden',
    }}>
      {/* 탭 바 — role=tablist/tab · aria-selected, 좌우 화살표 · Home/End 로 옮긴다(감사 card-controltower#78) */}
      <div style={{
        display: 'flex', alignItems: 'center', borderBottom: `1px solid ${t.border}`,
        padding: `0 ${SPACING.lg}px`, overflowX: 'auto',
      }}>
        <div role="tablist" aria-label="가격 조회 결과 분류" onKeyDown={onTabKeyDown} style={{ display: 'flex', alignItems: 'center' }}>
        {tabButtons.map((cfg) => {
          const k = cfg.key;
          const on = k === activeKey;
          return (
            <button
              key={k}
              type="button"
              role="tab"
              id={tabId(k)}
              aria-selected={on}
              aria-controls={PANEL_ID}
              tabIndex={on ? 0 : -1}
              onClick={() => onTabChange(k)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none',
                padding: `${SPACING.sm}px ${SPACING.sm}px`, fontSize: FONT_SIZE.sm, cursor: 'pointer', minHeight: 44, // 탭 누르는 곳 44px(43px 였다, 감사 #518)
                whiteSpace: 'nowrap',
                borderBottom: `2px solid ${on ? cfg.color : 'transparent'}`,
                color: on ? t.textPrimary : t.textSecondary,
                fontWeight: on ? FONT_WEIGHT.bold : FONT_WEIGHT.medium,
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', background: on ? cfg.color : t.border }} />
              <span>{cfg.label}</span>
              <span style={{
                borderRadius: RADIUS.sm, padding: '1px 6px', fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
                background: on ? cfg.color : t.bgRaised, color: on ? textOnFill(cfg.color) : t.textSecondary,
              }}>
                {/* 첫 로딩 중(아직 모르는 수)도 0 이 아니라 '—'(시우 검수 2026-11-02 낮음). */}
                {cardTabsFallback || firstLoad ? '—' : cfg.count.toLocaleString()}
              </span>
            </button>
          );
        })}
        </div>
        {!cardTabsFallback && (
          <span style={{ marginLeft: 'auto', paddingLeft: SPACING.md, fontSize: FONT_SIZE.xs, color: t.textSecondary, whiteSpace: 'nowrap', flex: 'none' }}>
            {active.meta}
          </span>
        )}
      </div>

      <div role="tabpanel" id={PANEL_ID} aria-labelledby={tabId(activeKey)}>
      {cardTabsFallback ? cardTabsFallback : (
      <>

      {/* 탭 설명 */}
      <div style={{
        padding: `${SPACING.xs}px ${SPACING.lg}px`, background: t.bgRaised, borderBottom: `1px solid ${t.border}`,
        color: t.textSecondary, fontSize: FONT_SIZE.xs, lineHeight: LINE_HEIGHT.relaxed,
      }}>
        {active.note}
      </div>

      {/* 칩 조건 줄 — 칩 건수는 필터 전 전체, 여기는 필터 뒤 수(개편 §2-3). */}
      {viewFilter && (
        <div data-testid="drop-view-filter" style={{
          display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap',
          padding: `${SPACING.xxs}px ${SPACING.lg}px`, borderBottom: `1px solid ${t.border}`,
          fontSize: FONT_SIZE.xs, color: t.textSecondary,
        }}>
          <span>
            <b style={{ color: t.textPrimary }}>{viewLabel ?? '필터'}</b> 필터 후{' '}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{viewTotal.toLocaleString()}</span>건
          </span>
          {onClearView && (
            <AppButton variant="link" size="sm" style={phone ? { minHeight: 44 } : undefined} onClick={onClearView}>필터 지우기</AppButton>
          )}
        </div>
      )}

      {focus && !firstLoad && (
        <div role="status" data-testid="drop-focus-note" style={{
          display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap',
          padding: `${SPACING.xs}px ${SPACING.lg}px`, background: t.tintInfo, borderBottom: `1px solid ${t.lineAccent}`,
          fontSize: FONT_SIZE.xs, color: t.textPrimary, lineHeight: LINE_HEIGHT.relaxed,
        }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {focusTab
              ? `매장 가격 요청에서 온 카드 — ${focus.name}(${focus.code})를 '${TABS[focusTab].label}' 탭에 표시했습니다. ③ 스토어 반영으로 네이버 판매가를 바꿉니다(③ 은 반영 대상 전체를 함께 보냅니다).`
              : `${focus.name}(${focus.code})는 지금 가격 조회 표에 없습니다 — ① 가격 받기 뒤 다시 보거나, 이미 네이버 판매가와 PriceHub 가격이 같아졌을 수 있습니다.`}
          </span>
          {onFocusClose && (
            <AppButton variant="link" size="sm" style={phone ? { minHeight: 44 } : undefined} onClick={onFocusClose}>닫기</AppButton>
          )}
        </div>
      )}
      {reloading && (
        <div role="progressbar" aria-label="목록을 다시 받는 중" data-testid="drop-reloading"
          style={{ height: 2, background: t.accent, opacity: 0.6 }} className="app-skeleton" />
      )}
      {firstLoad ? <TableSkeleton t={t} testId="drop-skeleton" /> : (
      <>
      <TablePager paging={paging} placement="top" label={active.label} />
      <Table<Card>
        rowKey="id"
        size="small"
        onRow={focusRow}
        columns={phone ? phoneColumns : tableColumns}
        dataSource={paging.pageRows}
        pagination={false}
        virtual={virtual}
        scroll={virtual
          ? { y: virtualY, x: phone ? 340 : tableScrollX }
          : phone ? undefined : scrollY ? { y: scrollY, x: tableScrollX } : { x: tableScrollX }}
        locale={{
          emptyText: (
            <TableEmptyState icon="✓" t={t} compact
              message={viewFilter ? `${viewLabel ?? '필터'} 조건에 맞는 카드가 이 탭에 없습니다` : active.empty} />
          ),
        }}
      />
      <TablePager paging={paging} placement="bottom" label={active.label} />
      </>
      )}
      </>
      )}
      </div>
    </div>
  );
};
